import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { CustomerType, QuoteFlow, QuoteStatusId } from '../../../resources/base/models/enums';

const editableQuoteStatusIds = [QuoteStatusId['Web Initial Dealer']];
const savedOrValidatedQuoteStatusIds = [
  QuoteStatusId['Web Saved'],
  QuoteStatusId.Escalated,
  QuoteStatusId.Validated,
  QuoteStatusId.Approved,
];
const approvedQuoteStatusIds = [QuoteStatusId.Approved];
const unlockableQuoteStatusIds = [QuoteStatusId['Web Saved'], QuoteStatusId.Validated, QuoteStatusId.Escalated];
const unEditableQuoteStatusIds = [QuoteStatusId.Validated];
const nonTechnicalIdLength = 8;

const checkQuoteStatus = (quote: QuoteSchema.Schemas.QuoteResponse, statusesToCheckFor: QuoteStatusId[]) => {
  const status = quote.financialProduct?.offers?.[0]?.statusId;

  // TODO it is fallback untill BFT fix status on quote creation
  if (!status) {
    return true;
  }

  return status ? statusesToCheckFor.includes(status as QuoteStatusId) : false;
};

const updateQuoteDataWithNewIdAndStatus = (
  quote: QuoteSchema.Schemas.QuoteResponse,
  quoteFragment: QuoteSchema.Schemas.EditSalesQuoteResponse
): QuoteSchema.Schemas.QuoteResponse => {
  return {
    ...quote,
    financialProduct: {
      ...quote.financialProduct,
      offers: [
        {
          ...quote.financialProduct?.offers?.[0],
          status: quoteFragment.financialProduct?.offers?.[0]?.status,
          statusId: quoteFragment.financialProduct?.offers?.[0]?.statusId,
          offerId: quoteFragment.financialProduct?.offers?.[0]?.offerId,
          offerReference: quoteFragment.financialProduct?.offers?.[0]?.offerReference,
        },
      ],
    },
  };
};

export const isQuoteEditable = (quote: QuoteSchema.Schemas.QuoteResponse) => {
  return checkQuoteStatus(quote, editableQuoteStatusIds);
};

export const isQuoteUnlockable = (quote: QuoteSchema.Schemas.QuoteResponse) => {
  return checkQuoteStatus(quote, unlockableQuoteStatusIds);
};

export const isQuoteUnEditable = (quote: QuoteSchema.Schemas.QuoteResponse) => {
  return checkQuoteStatus(quote, unEditableQuoteStatusIds);
};

const initialState = {
  data: {} as QuoteSchema.Schemas.QuoteResponse,
  stakeholderContacts: [] as QuoteSchema.Schemas.StakeholderContact[],
  quoteFlow: QuoteFlow.NEW,
  promotionWarningMessages: [] as string[],
  productTypeRestore: null as { productTypeValue: string } | null,
  newCopiedQuoteSubmitted: false as boolean,
  pushVoWarningShown: false as boolean,
  availableRims: [] as VehicleIntegration.Components.Schemas.ModelOptions[],
};

type QuoteState = typeof initialState;

export const quoteSlice = createSlice({
  name: 'quote',
  initialState,
  selectors: {
    selectIsQuoteEditable: (state: QuoteState) => {
      return isQuoteEditable(state.data);
    },
    selectIsQuoteSavedOrValidated: (state: QuoteState) => {
      return checkQuoteStatus(state.data, savedOrValidatedQuoteStatusIds);
    },
    selectQuoteId: (state: QuoteState) => {
      return state.data.financialProduct?.offers?.[0].offerId;
    },
    selectQuoteReference: (state: QuoteState) => {
      return state.data.financialProduct?.offers?.[0].offerReference;
    },
    selectQuote: (state: QuoteState) => {
      return state.data;
    },
    selectQuoteFlow: (state: QuoteState) => {
      return state.quoteFlow;
    },
    selectQuoteWasAlreadyApproved: (state: QuoteState) => {
      return checkQuoteStatus(state.data, approvedQuoteStatusIds);
    },
    selectIsQuoteUnlockable: (state: QuoteState) => {
      return isQuoteUnlockable(state.data);
    },
    selectIsQuoteUnEditable: (state: QuoteState) => {
      return isQuoteUnEditable(state.data);
    },
    selectMilesCustomerIdFromQuote: (state: QuoteState) => {
      const id = state.data.customerData?.externalIdentifiers?.find(({ type }) => {
        return type === 'MILES_CUSTOMER_ID';
      })?.id;

      if ((id ?? '').length < nonTechnicalIdLength) return '';
      return id;
    },
    selectCustomerTypeFromQuote: (state: QuoteState) => {
      const bftCustomerType = state.data.customerData?.customerType;

      if (bftCustomerType === 'INDIVIDUAL') return CustomerType.INDIVIDUAL;
      if (bftCustomerType === 'LEGAL_ENTITY') return CustomerType.LEGAL_ENTITY;
      return undefined;
    },
    selectMilesVehicleId: (state: QuoteState) => {
      return state.data.vehicleData?.externalVehicleIdentifiers?.find(({ type }) => {
        return type === 'MILES_CATALOG_VEHICLE_ID';
      })?.id;
    },
    selectMilesModelId: (state: QuoteState) => {
      return state.data.vehicleData?.externalVehicleIdentifiers?.find(({ type }) => {
        return type === 'MILES_MODEL_ID';
      })?.id;
    },
    selectPromotionsWarningMessages: (state: QuoteState) => {
      return state.promotionWarningMessages;
    },
    selectProductName: (state: QuoteState) => {
      return state.data.financialProduct?.offers?.[0].productName;
    },
    selectMilesWarrantyType: (state: QuoteState) => {
      return state.data.financialProduct?.offers?.[0].milesWarrantyType;
    },
    selectIsNewCopiedQuoteSubmitted: (state: QuoteState) => {
      return state.newCopiedQuoteSubmitted;
    },
    selectIsPushVoWarningShown: (state: QuoteState) => {
      return state.pushVoWarningShown;
    },
    selectAvailableRims: (state: QuoteState) => {
      return state.availableRims;
    },
    selectStakeholderContacts: (state: QuoteState) => {
      return state.stakeholderContacts;
    },
    selectPreviousContractId: (state: QuoteState) => {
      return state.data.financialProduct?.offers?.[0].previousContractId;
    },
  },
  reducers: {
    setQuoteData(state, { payload }: PayloadAction<QuoteSchema.Schemas.QuoteResponse>) {
      return {
        ...state,
        data: {
          ...payload,
        },
      };
    },
    setQuoteFlow(state, { payload }: PayloadAction<QuoteFlow>) {
      state.quoteFlow = payload;
    },
    setIsNewCopiedQuoteSubmitted(state, { payload }: PayloadAction<boolean>) {
      state.newCopiedQuoteSubmitted = payload;
    },
    setIsPushVoWarningShown(state, { payload }: PayloadAction<boolean>) {
      state.pushVoWarningShown = payload;
    },
    resetQuote() {
      return initialState;
    },
    setProductTypeRestore(state, { payload }: PayloadAction<string | null>) {
      if (payload === null) {
        state.productTypeRestore = null;
      } else {
        state.productTypeRestore = { productTypeValue: payload };
      }
    },
    updateQuoteIdAndStatus(state, { payload }: PayloadAction<QuoteSchema.Schemas.EditSalesQuoteResponse>) {
      state.data = { ...updateQuoteDataWithNewIdAndStatus(state.data, payload) };
    },
    setPromotionsWarningMessages(state, { payload }: PayloadAction<string[]>) {
      state.promotionWarningMessages = payload;
    },
    setAvailableRims(state, { payload }: PayloadAction<VehicleIntegration.Components.Schemas.ModelOptions[]>) {
      state.availableRims = payload;
    },
    setStakeholderContacts(state, { payload }: PayloadAction<QuoteSchema.Schemas.StakeholderContact[]>) {
      state.stakeholderContacts = payload;
    },
  },
});

export const quoteSelectors = quoteSlice.selectors;
export const {
  setProductTypeRestore,
  setPromotionsWarningMessages,
  setQuoteData,
  setQuoteFlow,
  setAvailableRims,
  updateQuoteIdAndStatus,
  resetQuote,
  setIsNewCopiedQuoteSubmitted,
  setIsPushVoWarningShown,
  setStakeholderContacts,
} = quoteSlice.actions;
export const quoteReducer = quoteSlice.reducer;
